import { COHORTS, ONBOARDING_DEFS, PROGRAMS, ROLES, STEP_DEFINITIONS } from "../data/mockData";
import {
  AgeTrack,
  ApplicationDraft,
  ApplicationRoute,
  ApplicationStepDef,
  ApplicationStepId,
  CohortStatus,
  IconName,
  OnboardingStepDef,
  OnboardingStepId,
  ProgramRecord,
  RoleCategory,
} from "../types/domain";
export function makeReference(prefix = "CAA") {
  return `${prefix}-${new Date().getFullYear()}-${Math.floor(100000 + Math.random() * 900000)}`;
}

export function formatDate(value: string) {
  if (!value) return "Not selected";
  const date = new Date(`${value}T12:00:00`);
  return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" }).format(date);
}

export function calculateAge(dateOfBirth: string, atDate?: string) {
  if (!dateOfBirth) return null;
  const birth = new Date(`${dateOfBirth}T12:00:00`);
  const reference = atDate ? new Date(`${atDate}T12:00:00`) : new Date();
  if (Number.isNaN(birth.getTime()) || birth > reference) return null;
  let age = reference.getFullYear() - birth.getFullYear();
  const month = reference.getMonth() - birth.getMonth();
  if (month < 0 || (month === 0 && reference.getDate() < birth.getDate())) age -= 1;
  return age;
}

export function initials(firstName: string, lastName: string) {
  return `${firstName.trim()[0] || "C"}${lastName.trim()[0] || "A"}`.toUpperCase();
}

export function classNames(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(" ");
}

export function roleById(id: string) {
  return ROLES.find(role => role.id === id) || ROLES[0];
}

export function programById(id: string) {
  return PROGRAMS.find(program => program.id === id) || null;
}

export function cohortById(id: string) {
  return COHORTS.find(cohort => cohort.id === id) || null;
}

export function trackById(programId: string, trackId: string) {
  return programById(programId)?.tracks?.find(track => track.id === trackId) || null;
}

export function routeCategory(route: ApplicationRoute | ""): RoleCategory {
  if (route === "parent-child") return "family";
  if (route === "teaching") return "teaching";
  if (route === "proctor") return "assessment";
  if (route === "organization") return "organization";
  if (route === "institution") return "institution";
  if (route === "academy-staff") return "academy";
  return "learner";
}

export function defaultRoleForRoute(route: ApplicationRoute): string {
  const role = ROLES.find(item => item.route === route && item.access !== "invitation");
  return (
    role?.id ||
    (route === "parent-child"
      ? "parent"
      : route === "organization"
        ? "organization-owner"
        : route === "institution"
          ? "institution-owner"
          : route === "proctor"
            ? "exam-proctor"
            : route === "teaching"
              ? "instructor"
              : "learner")
  );
}

export function programEligibleForRoute(program: ProgramRecord, route: ApplicationRoute | "") {
  return Boolean(route && program.audiences.includes(route as ApplicationRoute));
}

export function requiresGuardian(draft: ApplicationDraft) {
  if (draft.route === "parent-child") return true;
  const program = programById(draft.programId);
  const track = trackById(draft.programId, draft.trackId);
  const cohort = cohortById(draft.cohortId);
  const dob = draft.route === "learner-self" ? draft.applicant.dateOfBirth : draft.learner.dateOfBirth;
  const age = calculateAge(dob, cohort?.startDate);
  if (track?.guardianMode === "parent-primary" || track?.guardianMode === "guardian-required") return true;
  if (track?.guardianMode === "adult") return false;
  if (track?.guardianMode === "conditional") return age !== null && age < 18;
  return age !== null && age < Math.max(18, program?.minAge || 18);
}

export function learnerAgeForDraft(draft: ApplicationDraft) {
  const cohort = cohortById(draft.cohortId);
  const dob = draft.route === "parent-child" ? draft.learner.dateOfBirth : draft.applicant.dateOfBirth;
  return calculateAge(dob, cohort?.startDate);
}

export function ageTrackEligibility(draft: ApplicationDraft, track: AgeTrack) {
  const dob = draft.route === "parent-child" ? draft.learner.dateOfBirth : draft.applicant.dateOfBirth;
  if (!dob) return { eligible: true, reason: "Add the learner’s date of birth to confirm this track." };
  const selectedCohort = cohortById(draft.cohortId);
  if (selectedCohort) {
    const age = calculateAge(dob, selectedCohort.startDate);
    const eligible = age !== null && age >= track.min && (track.max === undefined || age <= track.max);
    return {
      eligible,
      reason: eligible
        ? `Learner will be ${age} at the selected cohort start.`
        : `Learner will be ${age} at the selected cohort start; this track is for ${track.ages}.`,
    };
  }
  const matchingCohorts = COHORTS.filter(
    cohort =>
      cohort.programId === draft.programId && cohort.trackId === track.id && cohort.status !== "closed",
  ).filter(cohort => {
    const age = calculateAge(dob, cohort.startDate);
    return age !== null && age >= track.min && (track.max === undefined || age <= track.max);
  });
  if (matchingCohorts.length)
    return {
      eligible: true,
      reason: `Eligible for ${matchingCohorts.length} configured cohort${matchingCohorts.length === 1 ? "" : "s"}; exact age is rechecked after cohort selection.`,
    };
  const currentAge = calculateAge(dob);
  return {
    eligible: false,
    reason: `Current age is ${currentAge}; no configured cohort currently matches ${track.ages}.`,
  };
}

export function statusLabel(status: CohortStatus) {
  return status === "open"
    ? "Applications open"
    : status === "limited"
      ? "Limited spaces"
      : status === "waitlist"
        ? "Waitlist"
        : status === "planned"
          ? "Planned intake"
          : "Closed";
}

export function statusTone(status: CohortStatus): "green" | "orange" | "blue" | "red" {
  if (status === "open") return "green";
  if (status === "limited" || status === "waitlist") return "orange";
  if (status === "planned") return "blue";
  return "red";
}

export function percentage(value: number, total: number) {
  return total ? Math.round((value / total) * 100) : 0;
}

export function downloadFile(filename: string, content: string, type = "application/json") {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 250);
}

export function applicationStepsFor(draft: ApplicationDraft): ApplicationStepDef[] {
  const route = draft.route;
  if (!route)
    return [
      STEP_DEFINITIONS.route,
      STEP_DEFINITIONS.profile,
      STEP_DEFINITIONS.program,
      STEP_DEFINITIONS.cohort,
      STEP_DEFINITIONS.review,
    ];
  if (route === "parent-child")
    return [
      "route",
      "profile",
      "learner",
      "program",
      "cohort",
      "readiness",
      "availability",
      "funding",
      "guardian",
      "consent",
      "review",
    ].map(id => STEP_DEFINITIONS[id as ApplicationStepId]);
  if (route === "learner-self") {
    const steps: ApplicationStepId[] = [
      "route",
      "profile",
      "learner",
      "program",
      "cohort",
      "readiness",
      "availability",
      "funding",
    ];
    if (requiresGuardian(draft)) steps.push("guardian");
    steps.push("consent", "review");
    return steps.map(id => STEP_DEFINITIONS[id]);
  }
  if (route === "teaching")
    return ["route", "profile", "professional", "program", "availability", "checks", "consent", "review"].map(
      id => STEP_DEFINITIONS[id as ApplicationStepId],
    );
  if (route === "proctor")
    return ["route", "profile", "professional", "availability", "checks", "consent", "review"].map(
      id => STEP_DEFINITIONS[id as ApplicationStepId],
    );
  if (route === "organization" || route === "institution")
    return [
      "route",
      "profile",
      "entity",
      "program",
      "cohort",
      "readiness",
      "availability",
      "funding",
      "checks",
      "consent",
      "review",
    ].map(id => STEP_DEFINITIONS[id as ApplicationStepId]);
  return ["route", "profile", "checks", "consent", "review"].map(
    id => STEP_DEFINITIONS[id as ApplicationStepId],
  );
}

export function onboardingStepsFor(roleId: string): OnboardingStepDef[] {
  const role = roleById(roleId);
  const ids: OnboardingStepId[] = ["welcome", "security", "profile"];
  if (["family", "organization", "institution"].includes(role.category)) ids.push("relationships");
  ids.push("role-setup", "tools", "policies", "readiness", "ready");
  return ids.map(id => ONBOARDING_DEFS[id]);
}

export function workspaceRecordsFor(roleId: string, draft: ApplicationDraft) {
  const role = roleById(roleId);
  const selectedProgram = programById(draft.programId);
  const selectedCohort = cohortById(draft.cohortId);
  const base = [
    {
      id: `primary-${role.id}`,
      name: role.workspace,
      role: role.label,
      description: selectedProgram
        ? `${selectedProgram.name}${selectedCohort ? ` · ${selectedCohort.name}` : ""}`
        : role.description,
      icon:
        role.category === "family"
          ? ("heart" as IconName)
          : role.category === "organization"
            ? ("building" as IconName)
            : role.category === "institution"
              ? ("school" as IconName)
              : role.category === "teaching"
                ? ("badge" as IconName)
                : role.category === "assessment"
                  ? ("eye" as IconName)
                  : role.category === "academy"
                    ? ("shield" as IconName)
                    : ("graduation" as IconName),
    },
  ];
  if (role.category === "learner")
    base.push({
      id: "academy-learning",
      name: "My Academy Learning",
      role: "Learner",
      description: "Programs, Chatoner Class, eligible Labs, projects and credentials.",
      icon: "book" as IconName,
    });
  if (role.category === "family")
    base.push({
      id: "family-actions",
      name: "Family Action Centre",
      role: "Parent / guardian",
      description: "Consent, schedules, billing, family-safe progress and support.",
      icon: "child" as IconName,
    });
  if (["organization", "institution"].includes(role.category))
    base.push({
      id: "learner-preview",
      name: "Sponsored Learning Overview",
      role: "Aggregate, permissioned view",
      description: "Seats, cohorts, aggregate progress, governance and decision-ready evidence.",
      icon: "chart" as IconName,
    });
  return base;
}

export function onboardingDestination(roleId: string) {
  const role = roleById(roleId);
  if (role.category === "family")
    return { label: "Parent & Guardian Studio", path: "/parent", icon: "heart" as IconName };
  if (role.category === "teaching")
    return { label: "Instructor Studio", path: "/instructor", icon: "badge" as IconName };
  if (role.category === "assessment")
    return { label: "Exam Proctor / Assessment Studio", path: "/proctor", icon: "eye" as IconName };
  if (role.category === "organization")
    return { label: "Organization Hub", path: "/organization", icon: "building" as IconName };
  if (role.category === "institution")
    return { label: "Education Institution Hub", path: "/institution", icon: "school" as IconName };
  if (role.category === "academy")
    return { label: "Academy Operations", path: "/admin", icon: "shield" as IconName };
  return { label: "Learner Campus", path: "/workspace", icon: "graduation" as IconName };
}
